import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { 
  Package, Settings, Calculator, Save, RotateCcw, 
  DollarSign, Percent, Layers, Printer, Truck, Ruler,
  AlertCircle, CheckCircle, Loader2, ChevronDown, ChevronUp, Tag, MessageCircle
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import AdminHeader from './AdminHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('kaha_session');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Material inline form component
const MaterialForm = ({ material, onChange, onSave, onCancel, saving }) => (
  <div className="p-4 border-2 border-accent/50 rounded-lg space-y-3 bg-accent/5">
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs">Nombre</Label>
        <Input
          value={material.name}
          onChange={(e) => onChange({ ...material, name: e.target.value })}
          className="mt-1 h-9"
          placeholder="Ej: Sencillo 3 mm"
          data-testid="input-material-name"
        />
      </div>
      <div>
        <Label className="text-xs">Costo por m² (MXN)</Label>
        <Input
          type="number"
          step="0.01"
          value={material.cost_per_m2}
          onChange={(e) => onChange({ ...material, cost_per_m2: parseFloat(e.target.value) || 0 })}
          className="mt-1 h-9"
          data-testid="input-material-cost"
        />
      </div>
      <div>
        <Label className="text-xs">Tipo</Label>
        <Select value={material.type} onValueChange={(val) => onChange({ ...material, type: val })}>
          <SelectTrigger className="mt-1 h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sencillo">Sencillo</SelectItem>
            <SelectItem value="doble">Doble</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Grosor (mm)</Label>
        <Input
          type="number"
          step="0.5"
          value={material.thickness_mm}
          onChange={(e) => onChange({ ...material, thickness_mm: parseFloat(e.target.value) || 0 })}
          className="mt-1 h-9"
          data-testid="input-material-thickness"
        />
      </div>
    </div>
    <div>
      <Label className="text-xs">Descripción</Label>
      <Input
        value={material.description || ''}
        onChange={(e) => onChange({ ...material, description: e.target.value })}
        className="mt-1 h-9"
        placeholder="Descripción del material"
      />
    </div>
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={material.active}
          onChange={(e) => onChange({ ...material, active: e.target.checked })}
          className="w-4 h-4"
        />
        Activo
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={material.auto_quotable}
          onChange={(e) => onChange({ ...material, auto_quotable: e.target.checked })}
          className="w-4 h-4"
        />
        Auto-cotizable
      </label>
    </div>
    <div className="flex gap-2 justify-end">
      <Button variant="outline" size="sm" onClick={onCancel}>Cancelar</Button>
      <Button size="sm" onClick={onSave} disabled={saving || !material.name} className="bg-accent hover:bg-accent/90">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
        Guardar
      </Button>
    </div>
  </div>
);

// Shipping & Packaging Settings Section
const ShippingSettingsSection = () => {
  const [shippingSettings, setShippingSettings] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(true);
  const [savingShipping, setSavingShipping] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetchShippingSettings();
  }, []);

  const fetchShippingSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/shipping-settings`, { headers: getAuthHeaders() });
      setShippingSettings(res.data);
    } catch {
      setShippingSettings({
        max_package_length_cm: 120,
        max_package_width_cm: 80,
        max_package_height_cm: 60,
        max_package_weight_kg: 25,
        max_total_packages_for_autoquote: 10,
        packaging_extra_weight_kg: 0.15,
      });
    }
    setLoadingShipping(false);
  };

  const handleSaveShipping = async () => {
    setSavingShipping(true);
    try {
      await axios.post(`${API}/admin/shipping-settings`, shippingSettings, { headers: getAuthHeaders() });
      toast.success('Configuración de envío guardada');
    } catch {
      toast.error('Error al guardar configuración de envío');
    }
    setSavingShipping(false);
  };

  const update = (key, value) => setShippingSettings(prev => ({ ...prev, [key]: value }));

  if (loadingShipping) return <div className="p-4 text-center text-muted-foreground text-sm">Cargando...</div>;

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Truck className="w-5 h-5 text-accent" />
          <span className="font-semibold">Empaquetado y Envío</span>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {expanded && shippingSettings && (
        <div className="p-4 space-y-4 border-t">
          <p className="text-sm text-muted-foreground">
            Reglas que determinan si un pedido puede cotizarse automáticamente vía Envia, o requiere cotización manual.
          </p>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Largo máx. paquete (cm)</Label>
              <Input
                type="number" step="1"
                value={shippingSettings.max_package_length_cm}
                onChange={(e) => update('max_package_length_cm', parseFloat(e.target.value) || 0)}
                className="mt-1 h-9"
                data-testid="shipping-max-length"
              />
            </div>
            <div>
              <Label className="text-xs">Ancho máx. paquete (cm)</Label>
              <Input
                type="number" step="1"
                value={shippingSettings.max_package_width_cm}
                onChange={(e) => update('max_package_width_cm', parseFloat(e.target.value) || 0)}
                className="mt-1 h-9"
                data-testid="shipping-max-width"
              />
            </div>
            <div>
              <Label className="text-xs">Alto máx. paquete (cm)</Label>
              <Input
                type="number" step="1"
                value={shippingSettings.max_package_height_cm}
                onChange={(e) => update('max_package_height_cm', parseFloat(e.target.value) || 0)}
                className="mt-1 h-9"
                data-testid="shipping-max-height"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Peso máx. paquete (kg)</Label>
              <Input
                type="number" step="0.5"
                value={shippingSettings.max_package_weight_kg}
                onChange={(e) => update('max_package_weight_kg', parseFloat(e.target.value) || 0)}
                className="mt-1 h-9"
                data-testid="shipping-max-weight"
              />
            </div>
            <div>
              <Label className="text-xs">Máx. bultos auto-cotización</Label>
              <Input
                type="number" step="1"
                value={shippingSettings.max_total_packages_for_autoquote}
                onChange={(e) => update('max_total_packages_for_autoquote', parseInt(e.target.value) || 1)}
                className="mt-1 h-9"
                data-testid="shipping-max-packages"
              />
            </div>
            <div>
              <Label className="text-xs">Peso extra empaque (kg)</Label>
              <Input
                type="number" step="0.05"
                value={shippingSettings.packaging_extra_weight_kg}
                onChange={(e) => update('packaging_extra_weight_kg', parseFloat(e.target.value) || 0)}
                className="mt-1 h-9"
                data-testid="shipping-extra-weight"
              />
            </div>
          </div>

          <Button
            onClick={handleSaveShipping}
            disabled={savingShipping}
            size="sm"
            className="bg-accent hover:bg-accent/90 gap-2"
            data-testid="btn-save-shipping-settings"
          >
            {savingShipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar Configuración de Envío
          </Button>
        </div>
      )}
    </div>
  );
};

// WhatsApp Settings Section
const WhatsAppSettingsSection = () => {
  const [waSettings, setWaSettings] = useState(null);
  const [loadingWa, setLoadingWa] = useState(true);
  const [savingWa, setSavingWa] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/whatsapp-settings`, { headers: getAuthHeaders() })
      .then(r => setWaSettings(r.data))
      .catch(() => setWaSettings({
        whatsapp_enabled: true,
        whatsapp_phone_number: '524778055212',
        whatsapp_display_number: '+52 477 805 5212',
        whatsapp_default_message: 'Hola, estoy viendo la página de KAHA y me gustaría recibir ayuda con mi pedido.',
        whatsapp_show_floating_button: true,
      }))
      .finally(() => setLoadingWa(false));
  }, []);

  const handleSave = async () => {
    setSavingWa(true);
    try {
      await axios.post(`${API}/admin/whatsapp-settings`, waSettings, { headers: getAuthHeaders() });
      toast.success('Configuración de WhatsApp guardada');
    } catch {
      toast.error('Error al guardar');
    }
    setSavingWa(false);
  };

  const update = (key, value) => setWaSettings(prev => ({ ...prev, [key]: value }));

  if (loadingWa) return <div className="p-4 text-center text-muted-foreground text-sm">Cargando...</div>;

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-green-600" />
          <span className="font-semibold">WhatsApp</span>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {expanded && waSettings && (
        <div className="p-4 space-y-4 border-t">
          <div className="flex items-center gap-3">
            <input type="checkbox" id="wa-enabled" checked={waSettings.whatsapp_enabled}
              onChange={(e) => update('whatsapp_enabled', e.target.checked)} className="w-4 h-4" />
            <Label htmlFor="wa-enabled" className="cursor-pointer text-sm">Habilitar canal de WhatsApp</Label>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="wa-floating" checked={waSettings.whatsapp_show_floating_button}
              onChange={(e) => update('whatsapp_show_floating_button', e.target.checked)} className="w-4 h-4" />
            <Label htmlFor="wa-floating" className="cursor-pointer text-sm">Mostrar botón flotante</Label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Número normalizado (sin +)</Label>
              <Input value={waSettings.whatsapp_phone_number}
                onChange={(e) => update('whatsapp_phone_number', e.target.value.replace(/\D/g, ''))}
                placeholder="524778055212" className="mt-1 h-9 font-mono" data-testid="wa-phone-number" />
            </div>
            <div>
              <Label className="text-xs">Número visible</Label>
              <Input value={waSettings.whatsapp_display_number}
                onChange={(e) => update('whatsapp_display_number', e.target.value)}
                placeholder="+52 477 805 5212" className="mt-1 h-9" data-testid="wa-display-number" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Mensaje predeterminado</Label>
            <Input value={waSettings.whatsapp_default_message}
              onChange={(e) => update('whatsapp_default_message', e.target.value)}
              className="mt-1 h-9" data-testid="wa-default-message" />
          </div>
          <Button onClick={handleSave} disabled={savingWa} size="sm" className="bg-green-600 hover:bg-green-700 gap-2"
            data-testid="btn-save-wa-settings">
            {savingWa ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar WhatsApp
          </Button>
        </div>
      )}
    </div>
  );
};

const AdminCostPanel = ({ adminUser }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    dimensions: true,
    materials: true,
    conversion: true,
    print: true,
    indirect: true,
    margin: true,
    volume: true,
    calibration: false,
    shipping: true
  });

  // Materials state
  const [materials, setMaterials] = useState([]);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [newMaterial, setNewMaterial] = useState(null);
  const [savingMaterial, setSavingMaterial] = useState(false);

  // Simulator state
  const [simulator, setSimulator] = useState({
    box_length: 535,
    box_width: 350,
    box_height: 222,
    quantity: 1,
    material_id: null,
    has_printing: false,
    coverage_percent: 0,
    custom_sheet_width: 600,
    custom_sheet_length: 1820,
    useCustomSheet: true
  });
  const [simulatorResult, setSimulatorResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchMaterials();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/pricing-settings`, { headers: getAuthHeaders() });
      setSettings(response.data);
    } catch (error) {
      toast.error('Error al cargar configuración');
    }
    setLoading(false);
  };

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/admin/materials`, { headers: getAuthHeaders() });
      setMaterials(response.data);
      // Auto-select first material for simulator
      if (response.data.length > 0 && !simulator.material_id) {
        setSimulator(prev => ({ ...prev, material_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pricing-settings`, settings, { headers: getAuthHeaders() });
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
      console.error('Save error:', error);
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('¿Estás seguro de restablecer los valores por defecto?')) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/pricing-settings/reset`, {}, { headers: getAuthHeaders() });
      setSettings(response.data.settings);
      toast.success('Configuración restablecida');
    } catch (error) {
      toast.error('Error al restablecer');
      console.error('Reset error:', error);
    }
    setLoading(false);
  };

  const runSimulator = async () => {
    setSimulating(true);
    try {
      const payload = {
        box_length: simulator.box_length,
        box_width: simulator.box_width,
        box_height: simulator.box_height,
        quantity: simulator.quantity,
        material_id: simulator.material_id,
        has_printing: simulator.has_printing,
        coverage_percent: simulator.coverage_percent,
        custom_sheet_width: simulator.useCustomSheet ? simulator.custom_sheet_width : null,
        custom_sheet_length: simulator.useCustomSheet ? simulator.custom_sheet_length : null
      };
      
      const response = await axios.post(`${API}/admin/quote-simulator`, payload, { headers: getAuthHeaders() });
      setSimulatorResult(response.data);
    } catch (error) {
      toast.error('Error en simulación');
    }
    setSimulating(false);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Material CRUD
  const handleSaveMaterial = async (mat) => {
    setSavingMaterial(true);
    try {
      if (mat.id) {
        // Update
        await axios.put(`${API}/admin/materials/${mat.id}`, mat, { headers: getAuthHeaders() });
        toast.success('Material actualizado');
      } else {
        // Create
        await axios.post(`${API}/admin/materials`, mat, { headers: getAuthHeaders() });
        toast.success('Material creado');
      }
      await fetchMaterials();
      setEditingMaterial(null);
      setNewMaterial(null);
    } catch (error) {
      toast.error('Error al guardar material');
    }
    setSavingMaterial(false);
  };

  const handleDeleteMaterial = async (id) => {
    if (!window.confirm('¿Eliminar este material?')) return;
    try {
      await axios.delete(`${API}/admin/materials/${id}`, { headers: getAuthHeaders() });
      toast.success('Material eliminado');
      await fetchMaterials();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const SectionHeader = ({ title, icon: Icon, section, children }) => (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-accent" />
          <span className="font-semibold">{title}</span>
        </div>
        {expandedSections[section] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {expandedSections[section] && (
        <div className="p-4 space-y-4 border-t">
          {children}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-manrope">
      <AdminHeader adminUser={adminUser} currentPage="costos" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex justify-end gap-3 mb-4">
          <Button variant="outline" onClick={handleReset} className="gap-2" size="sm">
            <RotateCcw className="w-4 h-4" />
            Restablecer
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2 bg-accent hover:bg-accent/90" size="sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </Button>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-primary mb-2">Configuración de Cotización</h2>
              <p className="text-muted-foreground">Ajusta los parámetros de precio para tu negocio</p>
            </div>

            {settings && (
              <div className="space-y-4">
                {/* Dimension Limits Section */}
                <SectionHeader title="Dimensiones mínimas y máximas" icon={Ruler} section="dimensions">
                  <p className="text-xs text-muted-foreground mb-4">
                    Define los límites de dimensiones (en mm) que los clientes verán en cm al cotizar su caja.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-primary">Largo</p>
                      <div>
                        <Label className="text-xs">Mínimo (mm) <span className="text-muted-foreground">= {((settings.dim_min_length ?? 420) / 10)} cm</span></Label>
                        <Input
                          type="number"
                          value={settings.dim_min_length ?? 420}
                          onChange={(e) => updateSetting('dim_min_length', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                          data-testid="input-dim-min-length"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Máximo (mm) <span className="text-muted-foreground">= {((settings.dim_max_length ?? 99999) / 10)} cm</span></Label>
                        <Input
                          type="number"
                          value={settings.dim_max_length ?? 99999}
                          onChange={(e) => updateSetting('dim_max_length', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                          data-testid="input-dim-max-length"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-primary">Ancho</p>
                      <div>
                        <Label className="text-xs">Mínimo (mm) <span className="text-muted-foreground">= {((settings.dim_min_width ?? 200) / 10)} cm</span></Label>
                        <Input
                          type="number"
                          value={settings.dim_min_width ?? 200}
                          onChange={(e) => updateSetting('dim_min_width', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                          data-testid="input-dim-min-width"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Máximo (mm) <span className="text-muted-foreground">= {((settings.dim_max_width ?? 2500) / 10)} cm</span></Label>
                        <Input
                          type="number"
                          value={settings.dim_max_width ?? 2500}
                          onChange={(e) => updateSetting('dim_max_width', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                          data-testid="input-dim-max-width"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-primary">Alto</p>
                      <div>
                        <Label className="text-xs">Mínimo (mm) <span className="text-muted-foreground">= {((settings.dim_min_height ?? 50) / 10)} cm</span></Label>
                        <Input
                          type="number"
                          value={settings.dim_min_height ?? 50}
                          onChange={(e) => updateSetting('dim_min_height', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                          data-testid="input-dim-min-height"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Máximo (mm) <span className="text-muted-foreground">= {((settings.dim_max_height ?? 99999) / 10)} cm</span></Label>
                        <Input
                          type="number"
                          value={settings.dim_max_height ?? 99999}
                          onChange={(e) => updateSetting('dim_max_height', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                          data-testid="input-dim-max-height"
                        />
                      </div>
                    </div>
                  </div>
                </SectionHeader>

                {/* Materials Section */}
                <SectionHeader title="Materiales" icon={Layers} section="materials">
                  <div className="space-y-3">
                    {materials.map((mat) => (
                      editingMaterial?.id === mat.id ? (
                        <MaterialForm
                          key={mat.id}
                          material={editingMaterial}
                          onChange={setEditingMaterial}
                          onSave={() => handleSaveMaterial(editingMaterial)}
                          onCancel={() => setEditingMaterial(null)}
                          saving={savingMaterial}
                        />
                      ) : (
                        <div key={mat.id} className={`flex items-center justify-between p-3 border rounded-lg ${mat.active ? '' : 'opacity-50'}`} data-testid={`material-row-${mat.id}`}>
                          <div>
                            <p className="font-semibold text-sm">{mat.name}</p>
                            <p className="text-xs text-muted-foreground">{mat.type === 'sencillo' ? 'Pared sencilla' : 'Doble pared'} · {mat.thickness_mm}mm</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-accent">${mat.cost_per_m2.toFixed(2)}/m²</span>
                            <Button variant="ghost" size="sm" onClick={() => setEditingMaterial({...mat})} data-testid={`edit-material-${mat.id}`}>
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteMaterial(mat.id)} className="text-red-500 hover:text-red-700">
                              ×
                            </Button>
                          </div>
                        </div>
                      )
                    ))}

                    {newMaterial ? (
                      <MaterialForm
                        material={newMaterial}
                        onChange={setNewMaterial}
                        onSave={() => handleSaveMaterial(newMaterial)}
                        onCancel={() => setNewMaterial(null)}
                        saving={savingMaterial}
                      />
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full gap-2"
                        onClick={() => setNewMaterial({ name: '', type: 'sencillo', thickness_mm: 3, cost_per_m2: 0, description: '', auto_quotable: true, active: true })}
                        data-testid="btn-add-material"
                      >
                        + Agregar Material
                      </Button>
                    )}

                    <div>
                      <Label>Desperdicio extra (%)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={settings.desperdicio_extra_pct}
                        onChange={(e) => updateSetting('desperdicio_extra_pct', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <input
                        type="checkbox"
                        id="una_lamina"
                        checked={settings.una_lamina_por_caja}
                        onChange={(e) => updateSetting('una_lamina_por_caja', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="una_lamina" className="cursor-pointer">
                        Una lámina por caja (sin imposición)
                      </Label>
                    </div>
                  </div>
                </SectionHeader>

                {/* Conversion Section */}
                <SectionHeader title="Conversión" icon={Settings} section="conversion">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Costo suaje por unidad (MXN)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={settings.costo_suaje_unitario}
                        onChange={(e) => updateSetting('costo_suaje_unitario', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Costo pegado por unidad (MXN)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={settings.costo_pegado_unitario}
                        onChange={(e) => updateSetting('costo_pegado_unitario', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Costo manejo por unidad (MXN)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={settings.costo_manejo_unitario}
                        onChange={(e) => updateSetting('costo_manejo_unitario', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Costo setup por unidad (MXN)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={settings.costo_setup_unitario}
                        onChange={(e) => updateSetting('costo_setup_unitario', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </SectionHeader>

                {/* Print Section */}
                <SectionHeader title="Impresión" icon={Printer} section="print">
                  <div className="space-y-4">
                    <div>
                      <Label>Modo de costo de impresión</Label>
                      <Select 
                        value={settings.modo_costo_impresion} 
                        onValueChange={(val) => updateSetting('modo_costo_impresion', val)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fijo">Costo fijo por unidad</SelectItem>
                          <SelectItem value="cobertura">Basado en cobertura</SelectItem>
                          <SelectItem value="tinta">Basado en consumo de tinta</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {settings.modo_costo_impresion === 'fijo' && (
                      <div>
                        <Label>Costo fijo de impresión por unidad (MXN)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={settings.costo_impresion_fijo_unitario}
                          onChange={(e) => updateSetting('costo_impresion_fijo_unitario', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                    )}
                    
                    {settings.modo_costo_impresion === 'cobertura' && (
                      <div>
                        <Label>Tarifa por m² a 100% cobertura (MXN)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={settings.tarifa_cobertura_por_m2}
                          onChange={(e) => updateSetting('tarifa_cobertura_por_m2', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                    )}
                    
                    {settings.modo_costo_impresion === 'tinta' && (
                      <div>
                        <Label>Costo por ml de tinta (MXN)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={settings.costo_por_ml_tinta}
                          onChange={(e) => updateSetting('costo_por_ml_tinta', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label>Cobertura por defecto (%)</Label>
                      <Slider
                        value={[settings.cobertura_default_pct]}
                        onValueChange={(val) => updateSetting('cobertura_default_pct', val[0])}
                        min={0}
                        max={100}
                        step={5}
                        className="mt-2"
                      />
                      <p className="text-sm text-muted-foreground mt-1">{settings.cobertura_default_pct}%</p>
                    </div>
                  </div>
                </SectionHeader>

                {/* Indirect Costs Section */}
                <SectionHeader title="Costos Indirectos" icon={DollarSign} section="indirect">
                  <div className="space-y-4">
                    <div>
                      <Label>Tipo de costo indirecto</Label>
                      <Select 
                        value={settings.tipo_costo_indirecto} 
                        onValueChange={(val) => updateSetting('tipo_costo_indirecto', val)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fijo">Costo fijo por unidad</SelectItem>
                          <SelectItem value="porcentaje">Porcentaje del costo directo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {settings.tipo_costo_indirecto === 'fijo' && (
                      <div>
                        <Label>Costo indirecto fijo por unidad (MXN)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={settings.valor_costo_indirecto_fijo}
                          onChange={(e) => updateSetting('valor_costo_indirecto_fijo', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                    )}
                    
                    {settings.tipo_costo_indirecto === 'porcentaje' && (
                      <div>
                        <Label>Porcentaje de costo indirecto (%)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={settings.valor_costo_indirecto_pct}
                          onChange={(e) => updateSetting('valor_costo_indirecto_pct', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                </SectionHeader>

                {/* Margin Section */}
                <SectionHeader title="Margen e IVA" icon={Percent} section="margin">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Margen objetivo (%)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={settings.margen_objetivo_pct}
                        onChange={(e) => updateSetting('margen_objetivo_pct', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Se aplica una sola vez sobre el costo total</p>
                    </div>
                    <div>
                      <Label>IVA (%)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={settings.iva_pct}
                        onChange={(e) => updateSetting('iva_pct', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Comision terminal de pago (%)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={settings.comision_terminal_pago_pct ?? 3}
                        onChange={(e) => updateSetting('comision_terminal_pago_pct', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                        data-testid="input-terminal-fee"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Se absorbe en el precio unitario. El cliente NO ve esta comision como linea separada.</p>
                    </div>
                  </div>
                </SectionHeader>

                {/* Volume Discount Section */}
                <SectionHeader title="Descuentos por Volumen" icon={Tag} section="volume">
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Descuentos ocultos para el cliente. Se aplican automáticamente al precio según la cantidad.
                    </p>
                    {(settings.descuentos_volumen || []).map((tier, index) => (
                      <div key={`tier-${tier.min_qty}-${tier.max_qty}`} className="flex items-center gap-3 p-3 border rounded-lg">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Desde (qty)</Label>
                            <Input
                              type="number"
                              value={tier.min_qty}
                              onChange={(e) => {
                                const tiers = [...(settings.descuentos_volumen || [])];
                                tiers[index] = { ...tiers[index], min_qty: parseInt(e.target.value) || 0 };
                                updateSetting('descuentos_volumen', tiers);
                              }}
                              className="mt-1 h-9"
                              data-testid={`discount-min-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Hasta (qty)</Label>
                            <Input
                              type="number"
                              value={tier.max_qty}
                              onChange={(e) => {
                                const tiers = [...(settings.descuentos_volumen || [])];
                                tiers[index] = { ...tiers[index], max_qty: parseInt(e.target.value) || 0 };
                                updateSetting('descuentos_volumen', tiers);
                              }}
                              className="mt-1 h-9"
                              data-testid={`discount-max-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Descuento (%)</Label>
                            <Input
                              type="number"
                              step="0.5"
                              value={tier.descuento_pct}
                              onChange={(e) => {
                                const tiers = [...(settings.descuentos_volumen || [])];
                                tiers[index] = { ...tiers[index], descuento_pct: parseFloat(e.target.value) || 0 };
                                updateSetting('descuentos_volumen', tiers);
                              }}
                              className="mt-1 h-9"
                              data-testid={`discount-pct-${index}`}
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 mt-5"
                          onClick={() => {
                            const tiers = (settings.descuentos_volumen || []).filter((_, i) => i !== index);
                            updateSetting('descuentos_volumen', tiers);
                          }}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        const tiers = [...(settings.descuentos_volumen || []), { min_qty: 0, max_qty: 0, descuento_pct: 0 }];
                        updateSetting('descuentos_volumen', tiers);
                      }}
                      data-testid="btn-add-discount-tier"
                    >
                      + Agregar Rango
                    </Button>
                  </div>
                </SectionHeader>

                {/* Calibration Section */}
                <SectionHeader title="Calibración" icon={Calculator} section="calibration">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <p className="text-sm text-amber-800">
                        La calibración permite ajustar el precio final para alinearlo con precios de mercado reales.
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="habilitar_calibracion"
                        checked={settings.habilitar_calibracion}
                        onChange={(e) => updateSetting('habilitar_calibracion', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="habilitar_calibracion" className="cursor-pointer">
                        Habilitar calibración manual
                      </Label>
                    </div>
                    {settings.habilitar_calibracion && (
                      <div>
                        <Label>Coeficiente de calibración</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={settings.coeficiente_calibracion}
                          onChange={(e) => updateSetting('coeficiente_calibracion', parseFloat(e.target.value) || 1)}
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          1.0 = sin cambio, 0.9 = -10%, 1.1 = +10%
                        </p>
                      </div>
                    )}
                  </div>
                </SectionHeader>

                {/* Shipping & Packaging Section */}
                <ShippingSettingsSection />

                {/* WhatsApp Settings Section */}
                <WhatsAppSettingsSection />
              </div>
            )}
          </div>

          {/* Simulator Panel */}
          <div className="space-y-6">
            <Card className="shadow-soft sticky top-24">
              <CardHeader className="border-b bg-primary text-white">
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Simulador de Cotización
                </CardTitle>
                <CardDescription className="text-white/80">
                  Prueba los cambios antes de guardar
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Simulator inputs */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Largo (mm)</Label>
                    <Input
                      type="number"
                      value={simulator.box_length}
                      onChange={(e) => setSimulator(prev => ({ ...prev, box_length: parseFloat(e.target.value) || 0 }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Ancho (mm)</Label>
                    <Input
                      type="number"
                      value={simulator.box_width}
                      onChange={(e) => setSimulator(prev => ({ ...prev, box_width: parseFloat(e.target.value) || 0 }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Alto (mm)</Label>
                    <Input
                      type="number"
                      value={simulator.box_height}
                      onChange={(e) => setSimulator(prev => ({ ...prev, box_height: parseFloat(e.target.value) || 0 }))}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Cantidad</Label>
                  <Input
                    type="number"
                    min={1}
                    value={simulator.quantity}
                    onChange={(e) => setSimulator(prev => ({ ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
                    className="mt-1"
                    data-testid="simulator-quantity"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="useCustomSheet"
                    checked={simulator.useCustomSheet}
                    onChange={(e) => setSimulator(prev => ({ ...prev, useCustomSheet: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="useCustomSheet" className="cursor-pointer text-sm">
                    Usar dimensiones de lámina personalizadas
                  </Label>
                </div>

                {simulator.useCustomSheet && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Ancho lámina (mm)</Label>
                      <Input
                        type="number"
                        value={simulator.custom_sheet_width}
                        onChange={(e) => setSimulator(prev => ({ ...prev, custom_sheet_width: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Largo lámina (mm)</Label>
                      <Input
                        type="number"
                        value={simulator.custom_sheet_length}
                        onChange={(e) => setSimulator(prev => ({ ...prev, custom_sheet_length: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Material</Label>
                  <Select
                    value={simulator.material_id || ''}
                    onValueChange={(val) => setSimulator(prev => ({ ...prev, material_id: val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccionar material" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials.map((mat) => (
                        <SelectItem key={mat.id} value={mat.id}>
                          {mat.name} (${mat.cost_per_m2}/m²)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="has_printing_sim"
                    checked={simulator.has_printing}
                    onChange={(e) => setSimulator(prev => ({ ...prev, has_printing: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="has_printing_sim" className="cursor-pointer text-sm">
                    Con impresión
                  </Label>
                  {simulator.has_printing && (
                    <Input
                      type="number"
                      value={simulator.coverage_percent}
                      onChange={(e) => setSimulator(prev => ({ ...prev, coverage_percent: parseFloat(e.target.value) || 0 }))}
                      className="w-20 ml-2"
                      placeholder="%"
                    />
                  )}
                </div>

                <Button 
                  onClick={runSimulator} 
                  disabled={simulating}
                  className="w-full bg-accent hover:bg-accent/90"
                >
                  {simulating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                  Calcular Cotización
                </Button>

                {/* Results */}
                {simulatorResult && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-lg">Resultado</span>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-600 text-sm">Calculado</span>
                      </div>
                    </div>

                    {/* Price summary */}
                    <div className="bg-accent/10 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span>Precio antes de IVA</span>
                        <span className="font-bold">${simulatorResult.final_price_before_tax.toFixed(2)} MXN</span>
                      </div>
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>IVA ({simulatorResult.iva_pct}%)</span>
                        <span>${simulatorResult.iva_amount.toFixed(2)} MXN</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>Precio con IVA</span>
                        <span className="text-accent">${simulatorResult.final_price_with_tax.toFixed(2)} MXN</span>
                      </div>
                    </div>

                    {/* Cost breakdown */}
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold">Desglose de costos:</p>
                      <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                        <span>Material:</span>
                        <span>{simulatorResult.selected_material_name} (${simulatorResult.material_cost_per_m2}/m²)</span>
                        <span>Lámina:</span>
                        <span>{simulatorResult.sheet_width_mm} × {simulatorResult.sheet_length_mm} mm</span>
                        <span>Área:</span>
                        <span>{simulatorResult.sheet_area_m2} m²</span>
                        <span>Material:</span>
                        <span>${simulatorResult.material_cost_per_unit.toFixed(2)}</span>
                        <span>Suaje:</span>
                        <span>${simulatorResult.die_cut_cost_per_unit.toFixed(2)}</span>
                        <span>Pegado:</span>
                        <span>${simulatorResult.glue_cost_per_unit.toFixed(2)}</span>
                        <span>Manejo:</span>
                        <span>${simulatorResult.handling_cost_per_unit.toFixed(2)}</span>
                        {simulatorResult.print_cost_per_unit > 0 && (
                          <>
                            <span>Impresión:</span>
                            <span>${simulatorResult.print_cost_per_unit.toFixed(2)}</span>
                          </>
                        )}
                        <span>Indirecto:</span>
                        <span>${simulatorResult.indirect_cost_per_unit.toFixed(2)}</span>
                        <span className="font-semibold">Costo directo:</span>
                        <span className="font-semibold">${simulatorResult.subtotal_direct_cost.toFixed(2)}</span>
                        <span>Margen ({simulatorResult.margin_pct}%):</span>
                        <span>+${(simulatorResult.price_before_discount - simulatorResult.subtotal_direct_cost - simulatorResult.indirect_cost_per_unit).toFixed(2)}</span>
                        {simulatorResult.volume_discount_pct > 0 && (
                          <>
                            <span className="text-green-600 font-semibold">Descuento vol. ({simulatorResult.volume_discount_pct}%):</span>
                            <span className="text-green-600 font-semibold">-${(simulatorResult.price_before_discount - simulatorResult.final_price_before_tax).toFixed(2)}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Benchmark comparison */}
                    <div className="bg-muted/50 rounded-lg p-3 text-sm">
                      <p className="font-semibold mb-1">Caso benchmark (535×350×222):</p>
                      <p className="text-muted-foreground">
                        Objetivo: ~$13.00 + IVA = ~$15.08 MXN
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCostPanel;
